import { useState } from 'react';
import { adminAPI, BACKEND_URL } from '../lib/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Building2, Users, FolderKanban, BarChart3, ShieldCheck,
  Crown, Settings2, LineChart, TrendingUp, HardHat, Trash2, Pencil, RotateCcw,
  Plus, UserPlus, LogIn, KeyRound, UserX
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useConfirm } from '../components/ui/confirm-dialog';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';

const PLAN_CONFIG = {
  starter: {
    label: 'Starter', color: '#94A3B8',
    limits: { projects: 2, scenarios: 3, users: 2 },
    features: ['Financial Intelligence (básico)', 'Hasta 2 proyectos', 'Hasta 3 escenarios por proyecto', '2 usuarios', 'Reportes básicos'],
  },
  pro: {
    label: 'Pro', color: '#6366F1',
    limits: { projects: 10, scenarios: 10, users: 5 },
    features: ['Financial Intelligence (completo)', 'Sensibilidad y Tornado', 'Hasta 10 proyectos', '10 escenarios por proyecto', '5 usuarios', 'Reportes avanzados', 'Copilot IA'],
  },
  business: {
    label: 'Business', color: '#0DBD8B',
    limits: { projects: 50, scenarios: 20, users: 15 },
    features: ['Todo de Pro', 'Market Intelligence', 'Construction Intelligence', 'Hasta 50 proyectos', '20 escenarios por proyecto', '15 usuarios', 'Decision Engine', 'API de exportación'],
  },
  enterprise: {
    label: 'Enterprise', color: '#F59E0B',
    limits: { projects: -1, scenarios: -1, users: -1 },
    features: ['Todo de Business', 'Proyectos ilimitados', 'Usuarios ilimitados', 'Portfolio Institucional', 'Invitación de empresas', 'White-label disponible', 'Soporte prioritario'],
  },
};

const MODULE_CONFIG = {
  financial: { label: 'Financial', icon: LineChart, color: 'var(--financial-primary)' },
  market: { label: 'Market', icon: TrendingUp, color: 'var(--market-primary)' },
  construction: { label: 'Construction', icon: HardHat, color: 'var(--construction-primary)' },
};

function KPI({ label, value, icon: Icon, color }) {
  return (
    <Card className="rounded-xl border-border/50 shadow-sm">
      <CardContent className="p-5 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}12` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-display font-semibold tabular-nums">{value}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function CompanyRow({ company, onPlanChange, onModuleToggle, onMakeInstitution, onRevokeInstitution, onEdit, onDelete, onResetMetrics, onRestoreMetrics, onManageUsers }) {
  const plan = PLAN_CONFIG[company.plan] || PLAN_CONFIG.starter;
  const modules = company.modules || {};
  // SWA-112 · si el archivo del logo ya no existe (404), mostrar la inicial, no una imagen rota
  const [logoBroken, setLogoBroken] = useState(false);
  // Normalize modules: can be {market: true} or {market: {active: true}}
  const isModuleActive = (key) => {
    const val = modules[key];
    if (typeof val === 'boolean') return val;
    if (typeof val === 'object' && val !== null) return val.active !== false;
    return false;
  };

  return (
    <TableRow data-testid={`admin-company-${company.id}`}>
      <TableCell>
        <div className="flex items-center gap-3">
          {company.logo_url && !logoBroken ? (
            <img src={`${BACKEND_URL}${company.logo_url}`} alt={company.name} onError={() => setLogoBroken(true)} data-testid="admin-company-logo" className="w-8 h-8 rounded-lg object-contain shrink-0" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)' }} />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: plan.color }}>
              {company.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div>
            <p className="text-sm font-medium">{company.name}</p>
            <p className="text-[10px] text-muted-foreground">{company.owner_email || 'Sin owner'}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Select value={company.plan || 'starter'} onValueChange={(val) => onPlanChange(company.id, val)}>
                  <SelectTrigger className="h-7 text-xs w-[120px]" data-testid={`plan-select-${company.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PLAN_CONFIG).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}><Badge style={{ background: `${cfg.color}15`, color: cfg.color, border: 'none' }} className="text-[10px]">{cfg.label}</Badge></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs p-3">
              <p className="text-xs font-semibold mb-1.5" style={{ color: plan.color }}>{plan.label}</p>
              <ul className="space-y-0.5">
                {plan.features?.map((f, i) => (
                  <li key={i} className="text-[10px] text-muted-foreground flex items-start gap-1">
                    <span style={{ color: plan.color }}>•</span> {f}
                  </li>
                ))}
              </ul>
              {plan.limits && (
                <div className="mt-2 pt-1.5 flex gap-3 text-[9px] font-medium" style={{ borderTop: '1px solid var(--border-default)' }}>
                  <span>{plan.limits.projects === -1 ? '∞' : plan.limits.projects} proyectos</span>
                  <span>{plan.limits.scenarios === -1 ? '∞' : plan.limits.scenarios} escenarios</span>
                  <span>{plan.limits.users === -1 ? '∞' : plan.limits.users} usuarios</span>
                </div>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          {Object.entries(MODULE_CONFIG).map(([key, cfg]) => {
            const active = isModuleActive(key);
            // Module dependency chain: construction → market → financial
            const financialOn = isModuleActive('financial');
            const marketOn = isModuleActive('market');
            const disabled = (key === 'market' && !financialOn) || (key === 'construction' && !marketOn);
            return (
              <TooltipProvider key={key} delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5">
                      <Switch
                        checked={active}
                        disabled={disabled}
                        onCheckedChange={(checked) => onModuleToggle(company.id, key, checked, modules)}
                        className="scale-75"
                        data-testid={`module-${key}-${company.id}`}
                      />
                      <cfg.icon className="w-3.5 h-3.5" style={{ color: active ? cfg.color : disabled ? '#E2E8F0' : '#D1D5DB' }} />
                    </div>
                  </TooltipTrigger>
                  {disabled && (
                    <TooltipContent side="top" className="text-[10px]">
                      {key === 'market' ? 'Requiere Financial Intelligence' : 'Requiere Market Intelligence'}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </TableCell>
      <TableCell className="text-center tabular-nums text-sm">{company.users_count}</TableCell>
      <TableCell className="text-center tabular-nums text-sm">{company.projects_count}</TableCell>
      <TableCell>
        {company.institution_id ? (
          <div className="flex items-center gap-2">
            <Badge className="text-[9px]" style={{ background: 'var(--warning-soft)', color: 'var(--warning-color)', border: 'none' }}>Institución</Badge>
            <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive" onClick={() => onRevokeInstitution(company.institution_id, company.name)} data-testid={`revoke-inst-${company.id}`}>
              Revocar
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={() => onMakeInstitution(company.id, company.name)} data-testid={`make-inst-${company.id}`}>
            <Crown className="w-3 h-3" /> Hacer institución
          </Button>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={() => onManageUsers(company)} data-testid={`manage-users-${company.id}`}>
            <Users className="w-3 h-3" /> Usuarios
          </Button>
          <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={() => onEdit(company)} data-testid={`edit-company-${company.id}`}>
            <Settings2 className="w-3 h-3" /> Editar
          </Button>
          {company.metrics_reset_at ? (
            <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" title={`Widgets desde: ${new Date(company.metrics_reset_at).toLocaleString('es-MX')}`} onClick={() => onRestoreMetrics(company.id, company.name)} data-testid={`restore-metrics-${company.id}`}>
              <RotateCcw className="w-3 h-3" style={{ color: 'var(--accent-primary)' }} /> Restaurar
            </Button>
          ) : (
            <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" title="Reinicia los contadores de widgets del dashboard de esta empresa (no borra datos)" onClick={() => onResetMetrics(company.id, company.name)} data-testid={`reset-metrics-${company.id}`}>
              <RotateCcw className="w-3 h-3" /> Reset widgets
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive" onClick={() => onDelete(company.id, company.name)} data-testid={`delete-company-${company.id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function SuperAdminPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['adminMetrics'],
    queryFn: () => adminAPI.metrics().then(r => r.data),
    staleTime: 30 * 1000,
  });

  const { data: companies = [], isLoading: companiesLoading } = useQuery({
    queryKey: ['adminCompanies'],
    queryFn: () => adminAPI.listCompanies().then(r => r.data),
    staleTime: 15 * 1000,
  });

  const handlePlanChange = async (companyId, plan) => {
    try {
      await adminAPI.updatePlan(companyId, plan);
      toast.success(`Plan actualizado a ${PLAN_CONFIG[plan]?.label}`);
      qc.invalidateQueries({ queryKey: ['adminCompanies'] });
      qc.invalidateQueries({ queryKey: ['adminMetrics'] });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleModuleToggle = async (companyId, moduleKey, checked, currentModules) => {
    const normalized = {};
    for (const [k, v] of Object.entries(currentModules)) {
      normalized[k] = typeof v === 'boolean' ? v : (v?.active !== false);
    }
    normalized[moduleKey] = checked;

    // Enforce dependency chain: construction → market → financial
    if (!checked) {
      // Deactivating financial → cascade deactivate market + construction
      if (moduleKey === 'financial') {
        normalized.market = false;
        normalized.construction = false;
      }
      // Deactivating market → cascade deactivate construction
      if (moduleKey === 'market') {
        normalized.construction = false;
      }
    }

    try {
      await adminAPI.updateModules(companyId, normalized);
      const deactivated = [];
      if (moduleKey === 'financial' && !checked) deactivated.push('Market', 'Construction');
      if (moduleKey === 'market' && !checked) deactivated.push('Construction');
      const msg = `${MODULE_CONFIG[moduleKey]?.label} ${checked ? 'activado' : 'desactivado'}`;
      toast.success(deactivated.length ? `${msg} (también: ${deactivated.join(', ')})` : msg);
      qc.invalidateQueries({ queryKey: ['adminCompanies'] });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleMakeInstitution = async (companyId, companyName) => {
    const ok = await confirm({
      title: `¿Convertir "${companyName}" en institución Enterprise?`,
      description: 'Esto le permite gestionar múltiples empresas subordinadas.',
      confirmLabel: 'Convertir',
    });
    if (!ok) return;
    try {
      const res = await adminAPI.makeInstitution(companyId);
      toast.success(res.data.message);
      qc.invalidateQueries({ queryKey: ['adminCompanies'] });
      qc.invalidateQueries({ queryKey: ['adminMetrics'] });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleRevokeInstitution = async (institutionId, companyName) => {
    const ok = await confirm({
      title: `¿Revocar estatus de institución para "${companyName}"?`,
      description: 'Se desvincularán todas las empresas asociadas.',
      confirmLabel: 'Revocar',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await adminAPI.removeInstitution(institutionId);
      toast.success('Institución revocada');
      qc.invalidateQueries({ queryKey: ['adminCompanies'] });
      qc.invalidateQueries({ queryKey: ['adminMetrics'] });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Edit company
  const [editCompany, setEditCompany] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', legal_name: '' });
  const [newTenantOpen, setNewTenantOpen] = useState(false);
  const [manageUsersCompany, setManageUsersCompany] = useState(null);

  const handleEditOpen = (company) => {
    setEditCompany(company);
    setEditForm({ name: company.name || '', legal_name: company.legal_name || '' });
  };

  const handleEditSave = async () => {
    if (!editCompany) return;
    try {
      await adminAPI.updateCompany(editCompany.id, editForm);
      toast.success('Empresa actualizada');
      setEditCompany(null);
      qc.invalidateQueries({ queryKey: ['adminCompanies'] });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    const ok = await confirm({
      title: `¿Eliminar "${companyName}" permanentemente?`,
      description: 'Se eliminarán todos sus usuarios, proyectos y datos. Esta acción NO se puede deshacer.',
      confirmLabel: 'Eliminar',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await adminAPI.deleteCompany(companyId);
      toast.success(`Empresa "${companyName}" eliminada`);
      qc.invalidateQueries({ queryKey: ['adminCompanies'] });
      qc.invalidateQueries({ queryKey: ['adminMetrics'] });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleResetMetrics = async (companyId, companyName) => {
    const ok = await confirm({
      title: `¿Reiniciar widgets de "${companyName}"?`,
      description: 'Los widgets del dashboard de esta empresa mostrarán solo la actividad posterior a este momento. Los datos históricos permanecen intactos y pueden restaurarse en cualquier momento desde este mismo botón.',
      confirmLabel: 'Reiniciar widgets',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await adminAPI.resetCompanyMetrics(companyId);
      toast.success(`Widgets de "${companyName}" reiniciados`);
      qc.invalidateQueries({ queryKey: ['adminCompanies'] });
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleRestoreMetrics = async (companyId, companyName) => {
    const ok = await confirm({
      title: `¿Restaurar métricas completas de "${companyName}"?`,
      description: 'Los widgets volverán a mostrar el histórico total, incluyendo actividad previa al reset anterior.',
      confirmLabel: 'Restaurar',
    });
    if (!ok) return;
    try {
      await adminAPI.restoreCompanyMetrics(companyId);
      toast.success(`Métricas de "${companyName}" restauradas`);
      qc.invalidateQueries({ queryKey: ['adminCompanies'] });
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  if (metricsLoading || companiesLoading) {
    return (
      <div className="px-6 lg:px-8 py-6">
        <Skeleton className="h-8 w-64 mb-6" />
        <div className="grid grid-cols-2 xl:grid-cols-6 gap-4 mb-8">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="px-6 lg:px-8 py-6 max-w-[1440px]" data-testid="super-admin-page">
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mb-7">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
          <h1 className="font-display text-2xl font-semibold tracking-tight">Administración de la Plataforma</h1>
        </div>
        <p className="text-sm text-muted-foreground">Gestión de empresas, planes, módulos e instituciones</p>
      </motion.div>

      {/* Metrics */}
      {metrics && (
        <div className="grid grid-cols-2 xl:grid-cols-6 gap-4 mb-8" data-testid="admin-metrics">
          <KPI label="Empresas" value={metrics.total_companies} icon={Building2} color="var(--accent-primary)" />
          <KPI label="Usuarios" value={metrics.total_users} icon={Users} color="#0DBD8B" />
          <KPI label="Proyectos" value={metrics.total_projects} icon={FolderKanban} color="#F59E0B" />
          <KPI label="Escenarios" value={metrics.total_scenarios} icon={Settings2} color="#64748B" />
          <KPI label="Análisis" value={metrics.total_analyses} icon={BarChart3} color="#6366F1" />
          <KPI label="Instituciones" value={metrics.total_institutions} icon={Crown} color="#F59E0B" />
        </div>
      )}

      {/* Plan Distribution */}
      {metrics?.plan_distribution && (
        <div className="flex items-center gap-4 mb-6">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Distribución:</span>
          {Object.entries(metrics.plan_distribution).map(([plan, count]) => {
            const cfg = PLAN_CONFIG[plan] || PLAN_CONFIG.starter;
            return (
              <Badge key={plan} className="text-[10px] gap-1" style={{ background: `${cfg.color}12`, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                {cfg.label}: {count}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Companies Table */}
      <Card className="rounded-xl border-border/50 shadow-sm" data-testid="admin-companies-table">
        <CardContent className="p-0">
          <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-default)' }}>
            <div>
              <h2 className="text-sm font-display font-semibold">Empresas Registradas</h2>
              <p className="text-[11px] text-muted-foreground mt-0.5">Administra planes, módulos y permisos institucionales</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">{companies.length} empresas</Badge>
              <Button size="sm" className="h-7 gap-1 text-xs" onClick={() => setNewTenantOpen(true)} data-testid="new-tenant-btn">
                <Plus className="w-3 h-3" /> Nuevo tenant
              </Button>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Empresa</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Plan</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Módulos</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-center">Usuarios</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-center">Proyectos</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Institución</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map(c => (
                <CompanyRow
                  key={c.id}
                  company={c}
                  onPlanChange={handlePlanChange}
                  onModuleToggle={handleModuleToggle}
                  onMakeInstitution={handleMakeInstitution}
                  onRevokeInstitution={handleRevokeInstitution}
                  onEdit={handleEditOpen}
                  onDelete={handleDeleteCompany}
                  onResetMetrics={handleResetMetrics}
                  onRestoreMetrics={handleRestoreMetrics}
                  onManageUsers={setManageUsersCompany}
                />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Company Dialog */}
      {editCompany && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditCompany(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="edit-company-dialog">
            <h3 className="text-lg font-display font-semibold mb-4">Editar empresa</h3>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Nombre comercial</label>
                <input className="w-full h-9 px-3 border rounded-lg text-sm" style={{ borderColor: 'var(--border-default)' }} value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} data-testid="edit-company-name" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Razón social</label>
                <input className="w-full h-9 px-3 border rounded-lg text-sm" style={{ borderColor: 'var(--border-default)' }} value={editForm.legal_name} onChange={e => setEditForm(f => ({...f, legal_name: e.target.value}))} data-testid="edit-company-legal" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" size="sm" onClick={() => setEditCompany(null)}>Cancelar</Button>
              <Button size="sm" className="text-white" style={{ background: 'var(--accent-primary)' }} onClick={handleEditSave} data-testid="edit-company-save">Guardar</Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Maintenance — QA Data Purge */}
      <QaPurgeCard />

      {/* Plan Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6" data-testid="plan-comparison">        {Object.entries(PLAN_CONFIG).map(([key, cfg]) => (
          <Card key={key} className="rounded-xl border-border/50 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: cfg.color }} />
                <span className="text-sm font-display font-semibold">{cfg.label}</span>
                <Badge className="ml-auto text-[9px]" style={{ background: `${cfg.color}12`, color: cfg.color, border: 'none' }}>
                  {metrics?.plan_distribution?.[key] || 0} empresas
                </Badge>
              </div>
              <div className="flex gap-3 mb-3 text-[10px] font-medium" style={{ color: cfg.color }}>
                <span>{cfg.limits.projects === -1 ? '∞' : cfg.limits.projects} proyectos</span>
                <span>{cfg.limits.users === -1 ? '∞' : cfg.limits.users} usuarios</span>
              </div>
              <ul className="space-y-1">
                {cfg.features.map((f, i) => (
                  <li key={i} className="text-[10px] text-muted-foreground flex items-start gap-1.5">
                    <span className="mt-0.5 shrink-0" style={{ color: cfg.color }}>•</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>
      <NewTenantDialog open={newTenantOpen} onOpenChange={setNewTenantOpen} />
      <ManageUsersDialog company={manageUsersCompany} onClose={() => setManageUsersCompany(null)} />
    </div>
  );
}


function QaPurgeCard() {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const confirm = useConfirm();
  const qc = useQueryClient();

  const runDryRun = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.purgeQaData(true);
      setScan(res.data);
      if (res.data.qa_users_total === 0 && res.data.orphan_companies_count === 0) {
        toast.success('Base limpia — no hay datos QA por purgar');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al escanear');
    }
    setLoading(false);
  };

  const executePurge = async () => {
    const ok = await confirm({
      title: '¿Confirmar purga de datos QA?',
      description: `Se borrarán ${scan?.safe_companies_count || 0} empresas 100% QA + ${scan?.orphan_companies_count || 0} huérfanas + ${scan?.mixed_qa_users_count || 0} usuarios QA sueltos, junto con TODOS sus proyectos, escenarios y análisis. Los usuarios y datos reales quedan intactos. Esta acción no se puede deshacer.`,
      confirmLabel: 'Purgar ahora',
      variant: 'destructive',
    });
    if (!ok) return;
    setPurging(true);
    try {
      const res = await adminAPI.purgeQaData(false);
      const totalDeleted = Object.values(res.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Purga completa: ${totalDeleted} registros eliminados`);
      setScan(null);
      qc.invalidateQueries({ queryKey: ['adminMetrics'] });
      qc.invalidateQueries({ queryKey: ['adminCompanies'] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al purgar');
    }
    setPurging(false);
  };

  return (
    <Card className="rounded-xl border-border/50 shadow-sm mt-6" data-testid="qa-purge-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#FEF3F2' }}>
            <Trash2 className="w-4 h-4" style={{ color: '#D92D20' }} />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-display font-semibold">Mantenimiento — Limpiar datos QA</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Elimina de forma definitiva cuentas y compañías generadas por pruebas automatizadas (correos <code className="text-[10px] px-1 py-0.5 rounded bg-muted">@stratika-qa.example.com</code>) y compañías huérfanas sin usuarios ni proyectos. Nunca toca datos reales.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <Button size="sm" variant="outline" onClick={runDryRun} disabled={loading || purging} data-testid="qa-purge-scan-btn">
            {loading ? 'Escaneando…' : (scan ? 'Volver a escanear' : 'Escanear datos QA')}
          </Button>
          {scan && (scan.safe_companies_count > 0 || scan.orphan_companies_count > 0 || scan.mixed_qa_users_count > 0) && (
            <Button size="sm" variant="destructive" onClick={executePurge} disabled={purging} data-testid="qa-purge-execute-btn">
              {purging ? 'Purgando…' : `Purgar ahora`}
            </Button>
          )}
        </div>

        {scan && (
          <div className="rounded-lg border border-border/60 p-3 bg-muted/20 text-xs" data-testid="qa-purge-scan-result">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <Stat label="Usuarios QA" value={scan.qa_users_total} />
              <Stat label="Empresas 100% QA" value={scan.safe_companies_count} />
              <Stat label="Empresas mixtas" value={scan.mixed_companies_count} hint="Se conservan" />
              <Stat label="Empresas huérfanas" value={scan.orphan_companies_count} />
            </div>
            {Object.keys(scan.impact || {}).length > 0 ? (
              <>
                <p className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Registros que se borrarán</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-0.5">
                  {Object.entries(scan.impact).map(([col, n]) => (
                    <div key={col} className="flex justify-between border-b border-border/30 py-0.5">
                      <span className="text-muted-foreground">{col}</span>
                      <span className="tabular-nums font-medium">{n.toLocaleString('es-MX')}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-emerald-700 font-medium">Nada por purgar — base limpia.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-md bg-background border border-border/50 px-3 py-2">
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className="text-lg font-display font-bold tabular-nums">{(value || 0).toLocaleString('es-MX')}</p>
      {hint && <p className="text-[9px] text-muted-foreground">{hint}</p>}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════
// New Tenant wizard
// ═══════════════════════════════════════════════════════════════════════

function NewTenantDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    business_name: '', legal_name: '', plan: 'starter',
    owner_email: '', owner_full_name: '', owner_password: '',
  });
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!form.business_name.trim() || !form.owner_email.trim() || !form.owner_password) {
      toast.error('Nombre de la empresa, email y contraseña son obligatorios');
      return;
    }
    if (form.owner_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setSubmitting(true);
    try {
      const res = await adminAPI.createTenant(form);
      toast.success(`Tenant "${res.data.business_name}" creado`);
      qc.invalidateQueries({ queryKey: ['adminCompanies'] });
      qc.invalidateQueries({ queryKey: ['adminMetrics'] });
      onOpenChange(false);
      setForm({ business_name: '', legal_name: '', plan: 'starter', owner_email: '', owner_full_name: '', owner_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear tenant');
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !submitting && onOpenChange(false)} data-testid="new-tenant-dialog">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-display font-semibold mb-1">Nuevo tenant</h3>
        <p className="text-xs text-muted-foreground mb-5">Crea una empresa nueva junto con su primer usuario Owner.</p>
        <div className="space-y-3">
          <Field label="Nombre comercial *">
            <input className="w-full h-9 px-3 border rounded-lg text-sm" value={form.business_name} onChange={e => setForm(f => ({...f, business_name: e.target.value}))} data-testid="new-tenant-business-name" />
          </Field>
          <Field label="Razón social">
            <input className="w-full h-9 px-3 border rounded-lg text-sm" value={form.legal_name} onChange={e => setForm(f => ({...f, legal_name: e.target.value}))} data-testid="new-tenant-legal-name" />
          </Field>
          <Field label="Plan inicial">
            <select className="w-full h-9 px-2 border rounded-lg text-sm bg-white" value={form.plan} onChange={e => setForm(f => ({...f, plan: e.target.value}))} data-testid="new-tenant-plan">
              {Object.entries(PLAN_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
          <div className="pt-3 border-t">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Owner inicial</p>
            <div className="space-y-3">
              <Field label="Email *">
                <input type="email" className="w-full h-9 px-3 border rounded-lg text-sm" value={form.owner_email} onChange={e => setForm(f => ({...f, owner_email: e.target.value}))} data-testid="new-tenant-owner-email" />
              </Field>
              <Field label="Nombre completo *">
                <input className="w-full h-9 px-3 border rounded-lg text-sm" value={form.owner_full_name} onChange={e => setForm(f => ({...f, owner_full_name: e.target.value}))} data-testid="new-tenant-owner-name" />
              </Field>
              <Field label="Contraseña temporal * (mín. 6)">
                <input type="text" className="w-full h-9 px-3 border rounded-lg text-sm font-mono" value={form.owner_password} onChange={e => setForm(f => ({...f, owner_password: e.target.value}))} data-testid="new-tenant-owner-password" />
              </Field>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>Cancelar</Button>
          <Button size="sm" className="text-white" style={{ background: 'var(--accent-primary)' }} onClick={handleSubmit} disabled={submitting} data-testid="new-tenant-submit">
            {submitting ? 'Creando…' : 'Crear tenant'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Manage Users (per-company) dialog
// ═══════════════════════════════════════════════════════════════════════

function ManageUsersDialog({ company, onClose }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { impersonate: doImpersonate } = useAuth();
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', full_name: '', role: 'analyst', password: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['adminCompanyUsers', company?.id],
    queryFn: () => adminAPI.listCompanyUsers(company.id).then(r => r.data.users),
    enabled: !!company,
  });

  if (!company) return null;
  const users = data || [];

  const refresh = () => qc.invalidateQueries({ queryKey: ['adminCompanyUsers', company.id] });

  const handleCreate = async () => {
    if (!newUser.email || !newUser.full_name || !newUser.password) {
      toast.error('Completa email, nombre y contraseña'); return;
    }
    if (newUser.password.length < 6) { toast.error('Contraseña mínima 6 caracteres'); return; }
    try {
      await adminAPI.createCompanyUser(company.id, newUser);
      toast.success('Usuario creado');
      setNewUser({ email: '', full_name: '', role: 'analyst', password: '' });
      setCreating(false);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleChangeRole = async (u, newRole) => {
    try {
      await adminAPI.changeUserRole(u.id, newRole);
      toast.success('Rol actualizado');
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (u) => {
    const ok = await confirm({
      title: `¿Eliminar usuario ${u.email}?`,
      description: 'Esta acción es reversible por 30 días vía soporte técnico.',
      confirmLabel: 'Eliminar', variant: 'destructive',
    });
    if (!ok) return;
    try {
      await adminAPI.deleteUser(u.id);
      toast.success('Usuario eliminado');
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleResetPassword = async (u) => {
    const newPw = prompt(`Nueva contraseña para ${u.email} (mín. 6):`);
    if (!newPw) return;
    if (newPw.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    try {
      await adminAPI.resetUserPassword(u.id, newPw);
      toast.success('Contraseña restablecida. Compártela con el usuario por canal seguro.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleImpersonate = async (u) => {
    const ok = await confirm({
      title: `¿Impersonar a ${u.email}?`,
      description: `Verás la plataforma como ${u.full_name} (rol ${u.role}). Todas tus acciones quedarán registradas en la auditoría con tu identidad de super admin.`,
      confirmLabel: 'Impersonar',
    });
    if (!ok) return;
    try {
      const res = await adminAPI.impersonate(u.id);
      await doImpersonate(res.data.token, res.data.user);
      toast.success(`Impersonando a ${u.email}`);
      onClose();
      navigate('/');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="manage-users-dialog">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-lg font-display font-semibold">Usuarios de {company.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{users.length} usuarios activos · Plan {PLAN_CONFIG[company.plan]?.label || company.plan}</p>
          </div>
          <Button size="sm" onClick={() => setCreating(true)} data-testid="add-tenant-user-btn"><UserPlus className="w-3 h-3 mr-1" /> Nuevo usuario</Button>
        </div>

        {creating && (
          <div className="p-3 rounded-lg border border-dashed border-primary/30 bg-muted/20 mb-4 space-y-2" data-testid="create-user-form">
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Email" className="h-8 px-2 border rounded text-xs" value={newUser.email} onChange={e => setNewUser(u => ({...u, email: e.target.value}))} data-testid="new-user-email" />
              <input placeholder="Nombre" className="h-8 px-2 border rounded text-xs" value={newUser.full_name} onChange={e => setNewUser(u => ({...u, full_name: e.target.value}))} data-testid="new-user-name" />
              <select className="h-8 px-2 border rounded text-xs bg-white" value={newUser.role} onChange={e => setNewUser(u => ({...u, role: e.target.value}))} data-testid="new-user-role">
                <option value="owner">Owner</option>
                <option value="analyst">Analista</option>
                <option value="viewer">Visor</option>
              </select>
              <input placeholder="Contraseña temporal (mín 6)" className="h-8 px-2 border rounded text-xs font-mono" value={newUser.password} onChange={e => setNewUser(u => ({...u, password: e.target.value}))} data-testid="new-user-password" />
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setCreating(false)}>Cancelar</Button>
              <Button size="sm" className="h-7 text-xs" onClick={handleCreate} data-testid="submit-new-user">Crear</Button>
            </div>
          </div>
        )}

        {isLoading ? <p className="text-xs text-muted-foreground">Cargando…</p> : (
          <div className="rounded-lg border border-border/60 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="text-[10px]">Usuario</TableHead>
                  <TableHead className="text-[10px]">Rol</TableHead>
                  <TableHead className="text-[10px]">Estado</TableHead>
                  <TableHead className="text-[10px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} className="text-xs" data-testid={`tenant-user-row-${u.id}`}>
                    <TableCell>
                      <div className="font-medium">{u.full_name}</div>
                      <div className="text-muted-foreground text-[10px]">{u.email}</div>
                    </TableCell>
                    <TableCell>
                      {u.role === 'super_admin' ? (
                        <Badge className="text-[10px]" style={{ background: '#F5F3FF', color: '#7C3AED', borderColor: '#7C3AED' }}>Super Admin</Badge>
                      ) : (
                        <select className="h-7 px-2 border rounded text-[11px] bg-white" value={u.role} onChange={e => handleChangeRole(u, e.target.value)} data-testid={`role-select-${u.id}`}>
                          <option value="owner">Owner</option>
                          <option value="analyst">Analista</option>
                          <option value="viewer">Visor</option>
                        </select>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-[10px]" style={{ color: u.is_active ? 'var(--success)' : 'var(--muted-foreground)' }}>
                        {u.is_active ? '● Activo' : '○ Inactivo'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {u.role !== 'super_admin' && (
                          <>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Impersonar" onClick={() => handleImpersonate(u)} data-testid={`impersonate-${u.id}`}>
                              <LogIn className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Reset password" onClick={() => handleResetPassword(u)} data-testid={`reset-pw-${u.id}`}>
                              <KeyRound className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" title="Eliminar" onClick={() => handleDelete(u)} data-testid={`delete-user-${u.id}`}>
                              <UserX className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex justify-end mt-5">
          <Button variant="outline" size="sm" onClick={onClose}>Cerrar</Button>
        </div>
      </motion.div>
    </div>
  );
}
